<template>
  <div class="col vehicle-schedule-edit-page">
    <div class="d-flex position-absolute ml-2">
      <div class="row align-items-center">
        <div class="color-chart-box bg-non-business-color"></div>
        <p>{{ __('vehicle_schedule.dealership_closed') }}</p>
      </div>
      <div class="row align-items-center">
        <div class="color-chart-box bg-green"></div>
        <p>{{ __('vehicle_schedule.vehicle_available') }}</p>
      </div>
    </div>
    <DeleteModal v-if="showDeleteModal" @hideModal="hideModal" @deleteRecord="deleteRecord">
      <template #title>
        {{ $t('models/conciergeAdviserSchedulePeriods.schedule.modals.delete_time_slot') }}
      </template>

      <template #content>
        {{
          $t('models/conciergeAdviserSchedulePeriods.schedule.modals.delete_time_slot_confirmation')
        }}
      </template>
    </DeleteModal>
    <FullCalendar :options="calendarOptions">
      <template v-slot:eventContent="event">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <div>
            <span class="">{{
              formatTime(event.event.start.getHours(), event.event.start.getMinutes())
            }}</span>
            -
            <span class="">{{
              formatTime(event.event.end.getHours(), event.event.end.getMinutes())
            }}</span>
          </div>
          <i class="fa fa-x mx-1"></i>
        </div>
      </template>
    </FullCalendar>
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import bootstrapPlugin from '@fullcalendar/bootstrap'
import frLocale from '@fullcalendar/core/locales/fr'
import DeleteModal from '@components/modals/DeleteModal.vue'
import { useForm } from '@inertiajs/vue3'

export default {
  name: 'VehicleScheduleEditPage',
  components: { FullCalendar, DeleteModal },
  props: {
    weekLength: {
      type: Number,
      default: 7
    },
    businessHours: {
      type: Array,
      required: true
    },
    vehicle: {
      type: Object,
      required: true
    },
    vehicleAvailabilities: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      statusForm: useForm({
        is_active: this.vehicle.is_active
      }),
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, bootstrapPlugin],
        headerToolbar: {
          left: '',
          center: '',
          right: ''
        },
        locale: frLocale,
        businessHours: this.businessHours,
        duration: { days: this.weekLength },
        dayHeaderFormat: { weekday: 'long' },
        initialView: 'timeGridWeek',
        initialEvents: this.vehicleAvailabilities,
        editable: true,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: this.weekLength > 5,
        slotMinTime: '05:00:00',
        slotMaxTime: '23:30:00',
        expandRows: true,
        select: this.handleDateSelect,
        eventClick: this.handleEventClick,
        eventsSet: this.handleEvents
      },
      currentEvents: [],
      deleteModalOptions: {
        title: 'Supprimer une plage horaire',
        content: 'Voulez-vous vraiment supprimer cette plage horaire?'
      },
      showDeleteModal: false,
      selectedEvent: null
    }
  },
  methods: {
    deleteRecord(selectedEvent) {
      this.selectedEvent.event.remove()
      this.showDeleteModal = false
    },
    hideModal() {
      this.showDeleteModal = false
    },
    handleDateSelect(selectInfo) {
      const title = ''
      let calendarApi = selectInfo.view.calendar

      calendarApi.unselect() // clear date selection

      const event = {
        id: this.createEventId(),
        title,
        start: selectInfo.startStr,
        end: selectInfo.endStr,
        allDay: selectInfo.allDay
      }
      calendarApi.addEvent(event)
    },

    handleEventClick(clickInfo) {
      if (this.$can('edit concierge vehicle schedules')) {
        this.selectedEvent = clickInfo
        this.showDeleteModal = true
      } else {
        alert("Vous n'avez pas les permissions nécessaires, veuillez contacter votre gestionnaire")
      }
    },

    handleEvents(events) {
      if (!this.$can('edit concierge vehicle schedules')) {
        alert("Vous n'avez pas les permissions nécessaires, veuillez contacter votre gestionnaire")
        return
      }

      this.currentEvents = events

      let newSchedules = []
      events.forEach(x => {
        newSchedules.push({
          start_time: this.formatTime(x.start.getHours(), x.start.getMinutes()),
          end_time: this.formatTime(x.end.getHours(), x.end.getMinutes()),
          weekday: new Date(x.start).getDay()
        })
      })

      const form = useForm({
        availabilities: newSchedules
      })
      form.patch(route('conciergeVehicles.schedules.update', this.vehicle.id), {
        preserveScroll: true
      })
    },
    createEventId() {
      return String(new Date().toISOString().replace(/T.*$/, ''))
    },
    formatTime(hours, minutes) {
      const hoursString = this.padNumber(hours)
      const minutesString = this.padNumber(minutes)
      return `${hoursString}:${minutesString}`
    },
    padNumber(value) {
      return value.toLocaleString('fr-CA', {
        minimumIntegerDigits: 2,
        useGrouping: false
      })
    }
  }
}
</script>

<style lang="scss">
.vehicle-schedule-edit-page {
  .fc .fc-daygrid-day.fc-day-today,
  .fc .fc-timegrid-col.fc-day-today {
    background-color: inherit !important;
  }

  .color-chart-box {
    width: 75px;
    height: 35.2px;
    border-radius: 0.25rem;
    box-sizing: border-box;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .color-chart-box + p {
    all: unset;
    margin-right: 3rem;
    margin-left: 1rem;
    font-weight: 700;
    color: #6c757d;
  }

  .bg-green {
    background-color: #4e9a60;
  }

  .bg-non-business-color {
    background-color: hsla(0, 0%, 84%, 0.3);
  }

  .fc-event-main {
    background-color: #4e9a60;
    outline-color: #4e9a60;
  }

  .fc-v-event {
    border-color: #4e9a60 !important;
  }
}
</style>
